import React from "react";

import {
  Box,
  Slider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { MuiColorInput } from "mui-color-input";

import { CaptionAnimation, SubtitleStyles } from "../../../../types";

interface CaptionAnimationControlsProps {
  styles: SubtitleStyles;
  onUpdateStyles: (styles: SubtitleStyles) => void;
}

const ANIMATIONS: { id: CaptionAnimation; label: string; hint: string }[] = [
  { id: "none", label: "None", hint: "Static captions" },
  { id: "highlight", label: "Highlight", hint: "Active word changes color" },
  { id: "box", label: "Box", hint: "Active word on a color block" },
  { id: "pop", label: "Pop", hint: "Active word pops and changes color" },
  { id: "karaoke", label: "Karaoke", hint: "Words fill in as they're spoken" },
  { id: "single_word", label: "1 word", hint: "One word at a time" },
];

const colorInputSx = {
  "& input": { width: 0, p: 0 },
  "& .MuiInputBase-root": { pr: 0.5 },
};

export default function CaptionAnimationControls({
  styles,
  onUpdateStyles,
}: CaptionAnimationControlsProps) {
  const animation = styles.caption_animation ?? "none";
  const maxWords = styles.max_words_per_caption ?? 0;
  const update = (patch: Partial<SubtitleStyles>) =>
    onUpdateStyles({ ...styles, ...patch, preset_id: "" });

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="overline" color="text.secondary">
          Animation
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={animation}
          onChange={(_, value: CaptionAnimation | null) =>
            value && update({ caption_animation: value })
          }
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 0.75,
            "& .MuiToggleButton-root": {
              border: 1,
              borderColor: "divider",
              borderRadius: "8px !important",
              textTransform: "none",
              py: 0.75,
            },
          }}
        >
          {ANIMATIONS.map((a) => (
            <ToggleButton key={a.id} value={a.id} title={a.hint}>
              {a.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary">
          {ANIMATIONS.find((a) => a.id === animation)?.hint}
        </Typography>
      </Box>

      {animation !== "none" && animation !== "single_word" && (
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {animation === "box" ? "Box color" : "Highlight color"}
            </Typography>
            <MuiColorInput
              format="hex"
              size="small"
              value={styles.highlight_color ?? "#FACC15"}
              onChange={(color) => update({ highlight_color: color })}
              sx={colorInputSx}
            />
          </Box>
          {animation === "box" && (
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Text on box
              </Typography>
              <MuiColorInput
                format="hex"
                size="small"
                value={styles.highlight_text_color ?? "#FFFFFF"}
                onChange={(color) => update({ highlight_text_color: color })}
                sx={colorInputSx}
              />
            </Box>
          )}
        </Stack>
      )}

      {animation !== "single_word" && (
        <Box>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              Words per caption
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              {maxWords === 0 ? "Auto" : maxWords}
            </Typography>
          </Stack>
          <Slider
            size="small"
            min={0}
            max={6}
            step={1}
            marks
            value={maxWords}
            onChange={(_, value) =>
              update({ max_words_per_caption: value as number })
            }
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => (v === 0 ? "Auto" : v)}
          />
        </Box>
      )}
    </Stack>
  );
}
